// The resident's landing screen right after they sign in with their email
// (see CommunityPicker.jsx's login flow) -- their own identity, request
// counts, recent activity, and this community's announcements, all in one
// GET /resident/dashboard round-trip (see riq.dashboard/loadDashboard in
// useResidentIQ.js, and legalcode_search/app/resident_dashboard.py for
// where each section's data actually comes from).
import React from "react";
import { Bell, Check, Clock, CreditCard, Mail, MapPin, MessageCircleQuestion, RefreshCw, Ticket } from "lucide-react";
import { Badge, Button, Display, Kicker, Rule } from "./ui.jsx";
import { categoryLabel, managerLabel } from "./triage.js";

function fmtDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
    " · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function fmtMoney(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount ?? "");
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

const StatTile = ({ label, value, tone }) => (
  <div style={{
    flex: 1, minWidth: 100, border: "1px solid var(--ink)",
    background: tone === "accent" ? "var(--accentTint)" : "var(--surface)",
    padding: "16px 14px", display: "flex", flexDirection: "column", gap: 6,
  }}>
    <span style={{ font: "800 30px/1 var(--bq-font-display)", letterSpacing: "-0.02em", color: "var(--ink)" }}>{value}</span>
    <span style={{ font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".12em", textTransform: "uppercase", color: "var(--muted2)" }}>{label}</span>
  </div>
);

const SectionHeader = ({ icon: Icon, children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
    <Icon size={15} color="var(--accent)" strokeWidth={2.25} style={{ flex: "none" }} />
    <Kicker tone="muted">{children}</Kicker>
  </div>
);

const EmptyNote = ({ children }) => (
  <p style={{ margin: 0, font: "400 12.5px/1.5 var(--bq-font-body)", color: "var(--muted)" }}>{children}</p>
);

export default function ResidentDashboard({ riq, session, onNewRequest, onBrowseTickets }) {
  const { dashboard, dashboardStatus, dashboardError, loadDashboard } = riq;

  // While the first load is in flight, fall back to whatever the session
  // already knows (set at login -- see session.js) so the header never
  // shows a blank name/email for a moment.
  const name = dashboard?.resident?.name || session?.resident?.name || session?.userName || "";
  const email = dashboard?.resident?.email || session?.resident?.email || "";
  const units = dashboard?.resident?.unit_addresses || [];

  const requests = dashboard?.requests || { open: 0, pending: 0, closed: 0, recent: [] };
  const lastComm = dashboard?.last_communication || null;
  const lastPayment = dashboard?.last_payment || null;
  const recentQuestions = dashboard?.recent_questions || [];
  const announcements = dashboard?.announcements || [];

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "14px 20px 24px", display: "flex", flexDirection: "column", gap: 22 }}>
      {/* ── identity ── */}
      <div style={{ borderTop: "1px solid var(--ink)", paddingTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
          <Display size={26}>{name || "Welcome"}</Display>
          <button
            onClick={loadDashboard}
            aria-label="Refresh dashboard"
            disabled={dashboardStatus === "loading"}
            style={{
              flex: "none", display: "flex", alignItems: "center", justifyContent: "center",
              width: 36, height: 36, border: "1px solid var(--rule2)", background: "transparent",
              color: "var(--muted2)", cursor: dashboardStatus === "loading" ? "default" : "pointer",
            }}>
            <RefreshCw size={16} strokeWidth={2} style={{ animation: dashboardStatus === "loading" ? "bq-pulse 1s linear infinite" : "none" }} />
          </button>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px" }}>
          {email && (
            <span style={{ display: "flex", alignItems: "center", gap: 6, font: "400 12.5px/1.3 var(--bq-font-body)", color: "var(--muted)" }}>
              <Mail size={13} strokeWidth={2} />{email}
            </span>
          )}
          {units.length > 0 && (
            <span style={{ display: "flex", alignItems: "center", gap: 6, font: "400 12.5px/1.3 var(--bq-font-body)", color: "var(--muted)" }}>
              <MapPin size={13} strokeWidth={2} />{units.join(" · ")}
            </span>
          )}
        </div>
      </div>

      {dashboardStatus === "error" && (
        <div role="alert" style={{ borderLeft: "3px solid var(--accent)", background: "var(--accentTint)", padding: "10px 12px", font: "500 12.5px/1.4 var(--bq-font-body)", color: "var(--accentDeep)" }}>
          {dashboardError || "Couldn't load your dashboard."}
        </div>
      )}

      {/* ── request counts ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <SectionHeader icon={Ticket}>Your requests</SectionHeader>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <StatTile label="Open" value={requests.open} tone="accent" />
          <StatTile label="Pending" value={requests.pending} />
          <StatTile label="Closed" value={requests.closed} />
        </div>
        <Button variant="secondary" onClick={onBrowseTickets} style={{ alignSelf: "flex-start" }}>My requests</Button>
      </div>

      <Rule />

      {/* ── last communication ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <SectionHeader icon={Clock}>Last communication</SectionHeader>
        {lastComm ? (
          <div style={{ border: "1px solid var(--rule2)", padding: "12px 13px", display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <span style={{ font: "700 13px/1.2 var(--bq-font-display)" }}>{lastComm.ticket_number}</span>
              {(lastComm.status || "").toLowerCase() === "closed" || (lastComm.status || "").toLowerCase() === "resolved"
                ? <Badge tone="solid"><Check size={10} strokeWidth={3.5} />{lastComm.status}</Badge>
                : <Badge tone="outline">{lastComm.status || "Open"}</Badge>}
            </div>
            {lastComm.summary && (
              <span style={{ font: "400 13px/1.4 var(--bq-font-body)", color: "var(--body)" }}>{lastComm.summary}</span>
            )}
            <span style={{ font: "400 11.5px/1.3 var(--bq-font-mono)", color: "var(--muted)" }}>
              {lastComm.recipient_email ? "To " + lastComm.recipient_email + " · " : ""}{fmtDateTime(lastComm.at)}
            </span>
          </div>
        ) : (
          <EmptyNote>No communication yet — nothing you've submitted has an update on record.</EmptyNote>
        )}
      </div>

      {/* ── last payment ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <SectionHeader icon={CreditCard}>Last payment</SectionHeader>
        {lastPayment ? (
          <div style={{ border: "1px solid var(--rule2)", padding: "12px 13px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <span style={{ font: "700 16px/1.2 var(--bq-font-display)" }}>{fmtMoney(lastPayment.amount)}</span>
              <span style={{ font: "400 11.5px/1.3 var(--bq-font-body)", color: "var(--muted)" }}>
                {[lastPayment.method, lastPayment.note].filter(Boolean).join(" · ")}
              </span>
            </div>
            <span style={{ font: "400 11.5px/1.3 var(--bq-font-mono)", color: "var(--muted)", flex: "none" }}>
              {fmtDateTime(lastPayment.paid_at)}
            </span>
          </div>
        ) : (
          <EmptyNote>No payment on file. Your community's records here reflect only what the board has entered.</EmptyNote>
        )}
      </div>

      <Rule />

      {/* ── recent questions ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <SectionHeader icon={MessageCircleQuestion}>Recent questions</SectionHeader>
        {recentQuestions.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {recentQuestions.map((q) => (
              <div key={q.id} style={{ padding: "9px 0", borderBottom: "1px solid var(--rule)", display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ font: "500 13px/1.4 var(--bq-font-body)", color: "var(--ink)" }}>{q.question}</span>
                <span style={{ font: "400 11px/1.2 var(--bq-font-mono)", color: "var(--muted)" }}>{fmtDateTime(q.asked_at)}</span>
              </div>
            ))}
          </div>
        ) : (
          <EmptyNote>Nothing asked yet — questions you ask ResidentIQ will show up here.</EmptyNote>
        )}
      </div>

      <Rule />

      {/* ── announcements / news feed ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <SectionHeader icon={Bell}>Announcements</SectionHeader>
        {announcements.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {announcements.map((a) => (
              <div key={a.id} style={{ padding: "11px 0", borderBottom: "1px solid var(--rule)", display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <span style={{ font: "700 13.5px/1.3 var(--bq-font-display)" }}>{a.title}</span>
                  <span style={{ font: "400 10.5px/1 var(--bq-font-mono)", color: "var(--muted)", flex: "none" }}>{fmtDateTime(a.posted_at)}</span>
                </div>
                {a.body && <span style={{ font: "400 12.5px/1.5 var(--bq-font-body)", color: "var(--body)" }}>{a.body}</span>}
                <span style={{ font: "600 9.5px/1 var(--bq-font-display)", letterSpacing: ".08em", textTransform: "uppercase", color: "var(--muted2)" }}>{a.posted_by}</span>
              </div>
            ))}
          </div>
        ) : (
          <EmptyNote>No announcements from the board yet.</EmptyNote>
        )}
      </div>

      <Button onClick={onNewRequest} style={{ alignSelf: "flex-start", marginTop: 4 }}>New request</Button>
    </div>
  );
}
